import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Tooltip,
  AppBar,
  Toolbar
} from '@mui/material';

import VisibilityIcon from '@mui/icons-material/Visibility';
import BarChartIcon from '@mui/icons-material/BarChart';
import EditIcon from '@mui/icons-material/Edit';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import DeleteIcon from '@mui/icons-material/Delete';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import PollIcon from '@mui/icons-material/Poll';
import QrCode2Icon from '@mui/icons-material/QrCode2';

import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';

import EditSurvey from './survey/EditSurvey';
import Resultado from './result/Resultado';
import ResultadoDetalle from './result/ResultadoDetalle';
import SurveyQR from './SurveyQR';
import ResultChart from './result/ResultChart';
import DatasetIcon from '@mui/icons-material/Dataset';
import TimelineIcon from '@mui/icons-material/Timeline';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
// ====================== Reusable Components ======================

const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  titleIcon,
  titleText,
  contentText,
  cancelText = 'Cancelar',
  confirmText,
  confirmColor = 'primary',
  confirmIcon,
  cancelIcon = <CloseIcon />,
}) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {titleIcon}
      {titleText}
    </DialogTitle>
    <DialogContent>
      <Typography>{contentText}</Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onClose} startIcon={cancelIcon}>
        {cancelText}
      </Button>
      <Button 
        onClick={onConfirm} 
        color={confirmColor} 
        variant="contained" 
        startIcon={confirmIcon}
      >
        {confirmText}
      </Button>
    </DialogActions>
  </Dialog>
);

const FullScreenDialog = ({
  open,
  onClose,
  titleIcon,
  titleText,
  children,
  closeText = 'Cerrar',
  closeIcon = <CloseIcon />,
}) => (
  <Dialog fullScreen open={open} onClose={onClose}>
    <AppBar sx={{ position: 'relative' }}>
      <Toolbar>
        {titleIcon && React.cloneElement(titleIcon, { sx: { mr: 2 } })}
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {titleText}
        </Typography>
        <IconButton edge="end" color="inherit" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Toolbar>
    </AppBar>
    <DialogContent>
      {children}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button 
        onClick={onClose} 
        variant="outlined" 
        startIcon={closeIcon}
        size="small"
      >
        {closeText}
      </Button>
    </DialogActions>
  </Dialog>
);

// ====================== SurveyCard ======================
const SurveyCard = ({ survey, onMenuClick, onCopyUrl, onShare, copiedId, t, locale }) => {
  const [isHovered, setIsHovered] = useState(false);

  const formatDate = (timestamp) => {
    if (!timestamp) return t('na');
    let date;
    try {
      if (timestamp instanceof Date) date = timestamp;
      else if (timestamp.toDate) date = timestamp.toDate();
      else if (timestamp.seconds) date = new Date(timestamp.seconds * 1000);
      else date = new Date(timestamp);

      return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return t('na');
    }
  };

  const questionCount = Object.keys(survey.questions || {}).length;
  const isActive = survey?.isActive || false;

  return (
    <Card 
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => onMenuClick(e, survey)}
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        position: 'relative',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        }
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, rgba(25,118,210,0.03) 0%, rgba(156,39,176,0.03) 100%)',
          opacity: 0,
          transition: 'opacity 0.3s',
          borderRadius: 'inherit',
        }}
      />

      <IconButton
        size="medium"
        onClick={(e) => { e.stopPropagation(); onMenuClick(e, survey); }}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 2,
          bgcolor: 'background.paper',
          boxShadow: 2,
        }}
      >
        <MoreVertIcon />
      </IconButton>

      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" fontWeight={600} sx={{ minHeight: '2.8em', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {survey.title || t('noTitle')}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'action.hover', px: 1.5, py: 0.5, borderRadius: 1 }}>
            <QuestionAnswerIcon fontSize="small" color="primary" />
            <Typography variant="body2">{t('questions', questionCount)}</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {formatDate(survey.createdAt)}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {survey.description || t('noDescription')}
        </Typography>
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', borderTop: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', px: 2, pb: 2 }}>
        <Chip 
          label={isActive ? t('active') : t('inactive')}
          color={isActive ? 'success' : 'default'}
          size="small"
          icon={isActive ? <ToggleOnIcon /> : <ToggleOffIcon />}
        />
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {survey.urlId && (
            <Tooltip title={copiedId === survey.urlId ? t('copied') : t('copyLink')} arrow>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onCopyUrl(survey.urlId); }}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={t('share')} arrow>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onShare(survey); }} disabled={!survey.urlId}>
              <ShareIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardActions>
    </Card>
  );
};

// ====================== MAIN DASHBOARD ======================
const Dashboard = () => {
  const { language, currentUser, isActive: subscriptionActive } = useAuth();

  const translations = useMemo(() => ({
    en: {
      noTitle: 'Untitled', na: 'N/A', noDescription: 'No description',
      active: 'Active', inactive: 'Inactive', copied: 'Copied!', copyLink: 'Copy link',
      share: 'Share', errorLoading: 'Error loading surveys', mySurveys: 'My Surveys',
      noSurveys: 'You have no surveys created', createFirst: 'Create your first survey to start',
      surveyActivated: 'Survey activated', errorToggle: 'Error changing survey status',
      surveyDeleted: 'Survey deleted successfully', errorDelete: 'Error deleting survey',
      surveyDeactivated: 'Survey deactivated', errorDeactivate: 'Error deactivating survey',
      linkCopied: 'Link copied!', participate: 'Participate in this survey',
      viewSurvey: 'View Survey', viewResults: 'View Results',
      viewResultChart: 'Results in Charts',                          // ← NUEVO
      viewDetailedResults: 'Detailed Results',
      edit: 'Edit', deactivate: 'Deactivate', activate: 'Activate', delete: 'Delete',
      confirmDelete: 'Confirm deletion', confirmDeactivate: 'Confirm deactivation',
      editSurvey: 'Edit Survey', surveyResults: 'Survey Results',
      resultCharts: 'Survey Results — Charts',                       // ← NUEVO
      detailedResults: 'Detailed Survey Results',
      cancel: 'Cancel', deactivateBtn: 'Deactivate', close: 'Close',
      requiresSubscription: 'This feature requires an active subscription',
      viewQR: 'QR Code', qrDialogTitle: 'Survey QR Code',
      questions: (count) => `${count} question${count !== 1 ? 's' : ''}`,
      sureDelete: (title) => `Are you sure you want to delete "${title}"?`,
      sureDeactivate: (title) => `Are you sure you want to deactivate "${title}"?`,
    },
    es: {
      noTitle: 'Sin título', na: 'N/A', noDescription: 'Sin descripción',
      active: 'Activa', inactive: 'Inactiva', copied: '¡Copiado!', copyLink: 'Copiar enlace',
      share: 'Compartir', errorLoading: 'Error al cargar las encuestas', mySurveys: 'Mis Encuestas',
      noSurveys: 'No tienes encuestas creadas', createFirst: 'Crea tu primera encuesta para comenzar',
      surveyActivated: 'Encuesta activada', errorToggle: 'Error al cambiar el estado de la encuesta',
      surveyDeleted: 'Encuesta eliminada correctamente', errorDelete: 'Error al eliminar la encuesta',
      surveyDeactivated: 'Encuesta desactivada', errorDeactivate: 'Error al desactivar la encuesta',
      linkCopied: '¡Enlace copiado!', participate: 'Participa en esta encuesta',
      viewSurvey: 'Ver Encuesta', viewResults: 'Ver Resultados',
      viewResultChart: 'Resultados en Gráficas',                     // ← NUEVO
      viewDetailedResults: 'Resultado detallado',
      edit: 'Editar', deactivate: 'Desactivar', activate: 'Activar', delete: 'Eliminar',
      confirmDelete: 'Confirmar eliminación', confirmDeactivate: 'Confirmar desactivación',
      editSurvey: 'Editar Encuesta', surveyResults: 'Resultados de la Encuesta',
      resultCharts: 'Resultados en Gráficas',                        // ← NUEVO
      detailedResults: 'Resultados Detallados de la Encuesta',
      cancel: 'Cancelar', deactivateBtn: 'Desactivar', close: 'Cerrar',
      requiresSubscription: 'Esta función requiere una suscripción activa',
      viewQR: 'Código QR', qrDialogTitle: 'Código QR de la encuesta',
      questions: (count) => `${count} pregunta${count !== 1 ? 's' : ''}`,
      sureDelete: (title) => `¿Estás seguro de eliminar "${title}"?`,
      sureDeactivate: (title) => `¿Estás seguro de desactivar "${title}"?`,
    }
  }), []);

  const lang = language === 'es' ? 'es' : 'en';
  const locale = lang === 'en' ? 'en-US' : 'es-ES';
  
  const t = useCallback((key, ...args) => {
    const value = translations[lang][key];
    return typeof value === 'function' ? value(...args) : value;
  }, [lang, translations]);

  // Estados
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deactivateDialog, setDeactivateDialog] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [resultChartOpen, setResultChartOpen] = useState(false);   // ← NUEVO
  const [detailedResultOpen, setDetailedResultOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  // Firestore Listener
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'surveys'),
      where('createdBy', '==', currentUser.uid),
      where('isDelete', '==', false),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        setSurveys(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching surveys:', err);
        setError(t('errorLoading'));
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, t]);

  // ==================== HANDLERS ====================
  const handleMenuClick = (event, survey) => {
    setAnchorEl(event.currentTarget);
    setSelectedSurvey(survey);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleViewSurvey = () => {
    if (selectedSurvey?.urlId) {
      window.open(`${window.location.origin}/surveyRes/${selectedSurvey.urlId}`, '_blank', 'noopener,noreferrer');
    }
    handleMenuClose();
  };

  const handleViewResults = () => { setResultOpen(true); handleMenuClose(); };

  // ← NUEVO
  const handleViewResultChart = () => { setResultChartOpen(true); handleMenuClose(); };

  const handleViewDetailedResults = () => {
    if (!subscriptionActive) return;
    setDetailedResultOpen(true);
    handleMenuClose();
  };

  const handleEditSurvey = () => { setEditOpen(true); handleMenuClose(); };
  const handleDeleteClick = () => { setDeleteDialog(true); handleMenuClose(); };
  const handleViewQR = () => { setQrOpen(true); handleMenuClose(); };

  const handleToggleActive = async () => {
    if (!selectedSurvey) return;
    if (selectedSurvey.isActive) {
      setDeactivateDialog(true);
      handleMenuClose();
      return;
    }
    try {
      await updateDoc(doc(db, 'surveys', selectedSurvey.id), { isActive: true });
      setSnackbar({ open: true, message: t('surveyActivated'), severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: t('errorToggle'), severity: 'error' });
    }
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSurvey) return;
    try {
      await updateDoc(doc(db, 'surveys', selectedSurvey.id), { isDelete: true, isActive: false });
      setSnackbar({ open: true, message: t('surveyDeleted'), severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: t('errorDelete'), severity: 'error' });
    }
    setDeleteDialog(false);
    setSelectedSurvey(null);
  };

  const handleDeactivateConfirm = async () => {
    if (!selectedSurvey) return;
    try {
      await updateDoc(doc(db, 'surveys', selectedSurvey.id), { isActive: false });
      setSnackbar({ open: true, message: t('surveyDeactivated'), severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: t('errorDeactivate'), severity: 'error' });
    }
    setDeactivateDialog(false);
    setSelectedSurvey(null);
  };

  const handleCopyUrl = (urlId) => {
    const url = `${window.location.origin}/surveyRes/${urlId}`;
    navigator.clipboard.writeText(url);
    setCopiedId(urlId);
    setSnackbar({ open: true, message: t('linkCopied'), severity: 'success' });
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleShare = async (survey) => {
    if (!survey.urlId) return;
    const url = `${window.location.origin}/surveyRes/${survey.urlId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: survey.title, text: survey.description || t('participate'), url });
      } catch (err) {
        if (err.name !== 'AbortError') handleCopyUrl(survey.urlId);
      }
    } else {
      handleCopyUrl(survey.urlId);
    }
  };

  // ==================== RENDER ====================
  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;
  }

  if (error) {
    return <Container maxWidth="lg" sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Container>;
  }

  const selectedSurveyUrl = selectedSurvey?.urlId
    ? `${window.location.origin}/surveyRes/${selectedSurvey.urlId}`
    : '';

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
 

      {surveys.length === 0 ? (
        <Box 
          textAlign="center" 
          py={10} 
          sx={{ 
            bgcolor: 'background.paper', 
            borderRadius: 3, 
            border: '2px dashed', 
            borderColor: 'divider',
            maxWidth: 520,
            mx: 'auto'
          }}
        >
          <PollIcon sx={{ fontSize: 90, color: 'text.disabled', mb: 3 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('noSurveys')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {t('createFirst')}
          </Typography>
          <Typography variant="body2" color="primary" fontWeight={500} sx={{ mb: 3 }}>
            {lang === 'es' 
              ? 'Por favor, haz clic en el botón "Crear" del menú lateral izquierdo para comenzar'
              : 'Please click the "Create" button in the left sidebar menu to start'
            }
          </Typography>
        </Box>
      ) : (
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(3,1fr)', lg: 'repeat(4,1fr)', xl: 'repeat(5,1fr)' },
          gap: 3 
        }}>
          {surveys.map((survey) => (
            <SurveyCard 
              key={survey.id}
              survey={survey}
              onMenuClick={handleMenuClick}
              onCopyUrl={handleCopyUrl}
              onShare={handleShare}
              copiedId={copiedId}
              t={t}
              locale={locale}
            />
          ))}
        </Box>
      )}

      {/* Menú */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleViewSurvey} sx={{ py: 1.5 }}>
          <ListItemIcon><SentimentVerySatisfiedIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText>{t('viewSurvey')}</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleViewResults} sx={{ py: 1.5 }}>
          <ListItemIcon><TimelineIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText>{t('viewResults')}</ListItemText>
        </MenuItem>

        {/* ← NUEVO: Resultados en Gráficas — disponible para todos */}
        <MenuItem onClick={handleViewResultChart} sx={{ py: 1.5 }}>
          <ListItemIcon><BarChartIcon fontSize="small" color="success" /></ListItemIcon>
          <ListItemText>{t('viewResultChart')}</ListItemText>
        </MenuItem>

        <Tooltip title={!subscriptionActive ? t('requiresSubscription') : ''} placement="left" arrow>
          <span>
            <MenuItem onClick={handleViewDetailedResults} disabled={!subscriptionActive} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <DatasetIcon fontSize="small" color={subscriptionActive ? "secondary" : "disabled"} />
              </ListItemIcon>
              <ListItemText>
                <Typography color={!subscriptionActive ? "text.disabled" : "inherit"}>
                  {t('viewDetailedResults')}
                </Typography>
              </ListItemText>
              {!subscriptionActive && (
                <Chip size="small" label="PRO" color="warning" sx={{ ml: 'auto', height: 20, fontSize: '0.7rem' }} />
              )}
            </MenuItem>
          </span>
        </Tooltip>

        <MenuItem onClick={handleEditSurvey} sx={{ py: 1.5 }}>
          <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText>{t('edit')}</ListItemText>
        </MenuItem>

        {selectedSurvey?.urlId && (
          <MenuItem onClick={handleViewQR} sx={{ py: 1.5 }}>
            <ListItemIcon><QrCode2Icon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText>{t('viewQR')}</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={handleToggleActive} sx={{ py: 1.5 }}>
          <ListItemIcon>
            {selectedSurvey?.isActive ? <ToggleOffIcon color="warning" /> : <ToggleOnIcon color="success" />}
          </ListItemIcon>
          <ListItemText>{selectedSurvey?.isActive ? t('deactivate') : t('activate')}</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleDeleteClick} sx={{ py: 1.5, color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>{t('delete')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <ConfirmationDialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        titleIcon={<DeleteIcon color="error" />}
        titleText={t('confirmDelete')}
        contentText={t('sureDelete', selectedSurvey?.title)}
        confirmText={t('delete')}
        confirmColor="error"
        confirmIcon={<DeleteIcon />}
      />

      <ConfirmationDialog
        open={deactivateDialog}
        onClose={() => setDeactivateDialog(false)}
        onConfirm={handleDeactivateConfirm}
        titleIcon={<ToggleOffIcon color="warning" />}
        titleText={t('confirmDeactivate')}
        contentText={t('sureDeactivate', selectedSurvey?.title)}
        confirmText={t('deactivateBtn')}
        confirmColor="warning"
        confirmIcon={<ToggleOffIcon />}
      />

      <FullScreenDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        titleIcon={<EditIcon />}
        titleText={t('editSurvey')}
      >
        <EditSurvey surveyId={selectedSurvey?.id} />
      </FullScreenDialog>

      <FullScreenDialog
        open={resultOpen}
        onClose={() => setResultOpen(false)}
        titleIcon={<BarChartIcon />}
        titleText={t('surveyResults')}
      >
        <Resultado surveyId={selectedSurvey?.id} />
      </FullScreenDialog>

      {/* ← NUEVO: Diálogo de Resultados en Gráficas */}
      <FullScreenDialog
        open={resultChartOpen}
        onClose={() => setResultChartOpen(false)}
        titleIcon={<BarChartIcon />}
        titleText={t('resultCharts')}
      >
        <ResultChart surveyId={selectedSurvey?.id} />
      </FullScreenDialog>

      <FullScreenDialog
        open={detailedResultOpen}
        onClose={() => setDetailedResultOpen(false)}
        titleIcon={<BarChartIcon />}
        titleText={t('detailedResults')}
      >
        <ResultadoDetalle surveyId={selectedSurvey?.id} />
      </FullScreenDialog>

      <Dialog
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, overflow: 'visible', background: 'transparent', boxShadow: 'none' } }}
      >
        <IconButton
          onClick={() => setQrOpen(false)}
          size="small"
          sx={{ position: 'absolute', top: -16, right: -16, bgcolor: 'background.paper', boxShadow: 2, zIndex: 1 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <SurveyQR url={selectedSurveyUrl} title={selectedSurvey?.title} description={selectedSurvey?.description} />
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Dashboard;